import { concat, find, propEq } from 'ramda';
import React, { FunctionComponent, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import { EmailType } from '../../../../common/enums/EmailType';
import { IBindingAction, IBindingCallback1 } from '../../../../common/models/callback';
import { IEmailTemplate } from '../../../../common/models/email/IEmailTemplate';
import { IAppState } from '../../../../common/models/store/IAppState';
import LoaderWrapper from '../../../../components/LoaderWrapper';
import ModalWindow from '../../../../components/ModalWindow';
import MessageTemplateEditor from '../../components/MessageTemplateEditor';
import { createEmailTemplateRoutine, fetchEmailTemplatesRoutine, updateEmailTemplateRoutine } from '../../routines';
import EmailPreview from '../EmailPreview';
import { refEmailFooter, refEmailHeader, refEmailLabel, refEmailLine } from '../../../../common/helpers/emailHelper';
import styles from './styles.module.scss';

interface IProps {
  emailTemplates: IEmailTemplate[];
  defaultEmailTemplates: IEmailTemplate[];
  isLoading: boolean;
  fetchEmailTemplates: IBindingAction;
  createEmailTemplate: IBindingCallback1<IEmailTemplate>;
  updateEmailTemplate: IBindingCallback1<IEmailTemplate>;
}

const MessagesPane: FunctionComponent<IProps> = ({
  isLoading,
  emailTemplates,
  defaultEmailTemplates,
  fetchEmailTemplates,
  createEmailTemplate,
  updateEmailTemplate
}) => {
  const [previewData, setPreviewData] = useState<undefined | { text: string; type: EmailType }>(undefined);
  useEffect(() => {
    if (!emailTemplates.length && !defaultEmailTemplates.length) {
      fetchEmailTemplates();
    }
  }, [emailTemplates, defaultEmailTemplates]);

  const findTemplate = (type: EmailType, templates: IEmailTemplate[]) => (
    (find(propEq('type', type), templates) as IEmailTemplate)
  );

  const onSave = (body: string, subject: string | null, type: EmailType) => {
    const template = findTemplate(type, emailTemplates);
    const updatedTemplate = { ...template, body, subject };

    if (template.id) {
      updateEmailTemplate(updatedTemplate);
    } else {
      createEmailTemplate(updatedTemplate);
    }
  };

  const onDefaultSave = (message: string, subject: string) => onSave(message, subject, EmailType.CandidateRefRequest);

  const onReminderSave = (message: string, subject: string) => onSave(
    message,
    subject,
    EmailType.CandidateRefRequestReminder
  );

  const onRefEmailSave = (message: string) => onSave(
    message,
    null,
    EmailType.ReferenceRefRequest
  );

  const emptyTemplate = { body: '', subject: '' };

  const candidateMessage = emailTemplates.length
    ? findTemplate(EmailType.CandidateRefRequest, emailTemplates) : emptyTemplate;
  const defaultCandidateMessage = defaultEmailTemplates.length
    ? findTemplate(EmailType.CandidateRefRequest, defaultEmailTemplates) : emptyTemplate;

  const reminderMessage = emailTemplates.length
    ? findTemplate(EmailType.CandidateRefRequestReminder, emailTemplates) : emptyTemplate;
  const defaultReminderMessage = defaultEmailTemplates.length
    ? findTemplate(EmailType.CandidateRefRequestReminder, defaultEmailTemplates) : emptyTemplate;

  const referenceMessage = emailTemplates.length
    ? findTemplate(EmailType.ReferenceRefRequest, emailTemplates) : emptyTemplate;
  const defaultReferenceMessage = defaultEmailTemplates.length
    ? findTemplate(EmailType.ReferenceRefRequest, defaultEmailTemplates) : emptyTemplate;

  return (
    <LoaderWrapper loading={isLoading || false} transparent>
      <ModalWindow
        isShown={Boolean(previewData)}
        onHide={() => setPreviewData(undefined)}
        contentClassName={styles.previewModal}
      >
        <EmailPreview type={previewData?.type as EmailType} text={previewData?.text as string} />
      </ModalWindow>
      <MessageTemplateEditor
        messageTemplate={candidateMessage.body}
        defaultMessageTemlpate={defaultCandidateMessage.body}
        subjectTemplate={candidateMessage.subject as string}
        defaultSubjectTemplate={defaultCandidateMessage.subject as string}
        messageTemplateHeader={
          <FormattedMessage id="emailPreview.firstName" defaultMessage="<Candidate first name>" />
        }
        messageTemplateFooter={
          <FormattedMessage id="emailPreview.candidateLink" defaultMessage="<link to provide references>" />
        }
        label={(
          <FormattedMessage id="messageTemplateSettings.default" defaultMessage="Default email to candidates" />
        )}
        onSave={onDefaultSave}
        onPreview={text => setPreviewData({ text, type: EmailType.CandidateRefRequest })}
      />

      <MessageTemplateEditor
        messageTemplate={reminderMessage.body}
        defaultMessageTemlpate={defaultReminderMessage.body}
        subjectTemplate={reminderMessage.subject as string}
        defaultSubjectTemplate={defaultReminderMessage.subject as string}
        messageTemplateHeader={
          <FormattedMessage id="emailPreview.firstName" defaultMessage="<Candidate first name>" />
        }
        messageTemplateFooter={
          <FormattedMessage id="emailPreview.candidateLink" defaultMessage="<link to provide references>" />
        }
        label={(
          <FormattedMessage
            id="messageTemplateSettings.reminder"
            defaultMessage="Default reminder email to candidates"
          />
        )}
        onSave={onReminderSave}
        onPreview={text => setPreviewData({ text, type: EmailType.CandidateRefRequestReminder })}
      />

      <MessageTemplateEditor
        subjectTemplate="Reference Check for [Candidate Full Name]"
        defaultSubjectTemplate="Reference Check for [Candidate Full Name]"
        messageTemplate={referenceMessage.body}
        defaultMessageTemlpate={defaultReferenceMessage.body}
        messageTemplateHeader={refEmailHeader}
        messageTemplateFooter={refEmailFooter}
        label={refEmailLabel}
        onSave={onRefEmailSave}
        onPreview={text => setPreviewData({
          text: concat(`${refEmailLine}\n\n`, text),
          type: EmailType.ReferenceRefRequest
        })}
        subjectDisabled
      />
    </LoaderWrapper>
  );
};

const mapStateToProps = ({
  settings: { emailTemplates: { emailTemplates, isLoading, defaultEmailTemplates } }
}: IAppState) => ({ emailTemplates, isLoading, defaultEmailTemplates });

const mapDispatchToProps = ({
  fetchEmailTemplates: fetchEmailTemplatesRoutine,
  createEmailTemplate: createEmailTemplateRoutine,
  updateEmailTemplate: updateEmailTemplateRoutine
});

export default connect(mapStateToProps, mapDispatchToProps)(MessagesPane);
